/** Amortization and desking math for Desking by EnvisionDMS.com. */

export function monthlyPayment(principal, aprPercent, termMonths) {
  const P = Number(principal) || 0;
  const n = Math.max(0, Math.round(Number(termMonths) || 0));
  const apr = Number(aprPercent) || 0;
  if (P <= 0 || n <= 0) return 0;
  if (apr <= 0) return P / n;
  const r = apr / 100 / 12;
  const denom = 1 - Math.pow(1 + r, -n);
  if (denom === 0) return 0;
  return (P * r) / denom;
}

export function financeCharge(principal, payment, termMonths) {
  const P = Number(principal) || 0;
  const pmt = Number(payment) || 0;
  const n = Math.max(0, Math.round(Number(termMonths) || 0));
  if (pmt <= 0 || n <= 0) return 0;
  return pmt * n - P;
}

export function amountFinanced({
  price = 0,
  fees = 0,
  insGap = 0,
  accessories = 0,
  vsc = 0,
  tax = 0,
  down = 0,
  trade = 0,
} = {}) {
  return (
    Number(price) +
    Number(fees) +
    Number(insGap) +
    Number(accessories) +
    Number(vsc) +
    Number(tax) -
    Number(down) -
    Number(trade)
  );
}

export function computeTax(taxableBase, taxRate) {
  const base = Math.max(0, Number(taxableBase) || 0);
  const rate = Number(taxRate) || 0;
  return base * rate;
}

/** Taxable base: price + accessories + vsc-ish products typically; demo uses price + accessories - trade. */
export function taxableBase({ price = 0, accessories = 0, trade = 0 } = {}) {
  return Math.max(0, Number(price) + Number(accessories) - Number(trade));
}

export function roundMoney(n) {
  return Math.round((Number(n) || 0) * 100) / 100;
}

/** Always print money with two decimal places (finance standard). */
export function formatMoney(n) {
  const v = Number(n) || 0;
  return v.toLocaleString("en-US", {
    style: "currency",
    currency: "USD",
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });
}

/** Value for <input type="number"> money/rate fields — always two decimals. */
export function formatDecimal(n, digits = 2) {
  const v = Number(n);
  if (!Number.isFinite(v)) return (0).toFixed(digits);
  return v.toFixed(digits);
}

export function formatPercent(n, digits = 2) {
  const v = Number(n) || 0;
  return `${v.toFixed(digits)}%`;
}

export function computeDealGross({
  price = 0,
  vehicleCost = 0,
  feeCost = 0,
  productProfit = 0,
  commissions = 0,
} = {}) {
  const frontGross =
    Number(price) - Number(vehicleCost) - Number(feeCost);
  const backGross = Number(productProfit);
  const totalGross = frontGross + backGross;
  const netProfit = totalGross - Number(commissions);
  return { frontGross, backGross, totalGross, netProfit };
}

/** Payment matrix: each down column uses its own amount financed. */
export function buildPaymentMatrix(structure, downs, terms) {
  return downs.map((down) => {
    const af = amountFinancedForDown(structure, down);
    return terms.map((term) => {
      const pmt = monthlyPayment(af, term.apr, term.months);
      return {
        amountFinanced: roundMoney(af),
        payment: roundMoney(pmt),
        financeCharge: roundMoney(financeCharge(af, pmt, term.months)),
      };
    });
  });
}

/** Recompute AF for a specific down column using structure totals except down. */
export function amountFinancedForDown(structure, down) {
  return amountFinanced({ ...structure, down });
}
